import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Snackbar,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import * as api from '../../services/api';
import { labelColors } from '../../theme';
import { Criteria, Essay, EssayPurpose, Label, Purpose, Transcription, User } from '../../types';
import ColumnFilter, { ColumnFilterRef } from '../common/ColumnFilter';
import SortableTable, { HeadCell } from '../common/SortableTable';
import Loading from '../Loading';

const parseUrlList = (raw: string | null): string[] =>
  raw
    ? raw
        .split(',')
        .map(s => s.trim())
        .filter(Boolean)
    : [];

const parseLocalDate = (raw: string): Date | null => {
  const ymd = raw.slice(0, 10);
  const [y, m, d] = ymd.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const LabelsManagement = () => {
  const [searchParams] = useSearchParams();
  const initialUrlFilters = useMemo(() => {
    const criteriaIds = parseUrlList(searchParams.get('criteriaId'));
    const typeIds = parseUrlList(searchParams.get('type'));
    const createdByIds = parseUrlList(searchParams.get('createdBy'));
    const purposeIds = parseUrlList(searchParams.get('purposeId'));
    const createdFrom = searchParams.get('createdFrom');
    const createdTo = searchParams.get('createdTo');
    const createdDate =
      createdFrom || createdTo
        ? {
            start: createdFrom ? parseLocalDate(createdFrom) : null,
            end: createdTo ? parseLocalDate(createdTo) : null,
          }
        : null;
    return { criteriaIds, typeIds, createdByIds, purposeIds, createdDate };
  }, [searchParams]);

  const [labels, setLabels] = useState<Label[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [criteria, setCriteria] = useState<Criteria[]>([]);
  const [essays, setEssays] = useState<Essay[]>([]);
  const [purposes, setPurposes] = useState<Purpose[]>([]);
  const [essayPurposes, setEssayPurposes] = useState<EssayPurpose[]>([]);
  const [transcriptions, setTranscriptions] = useState<Transcription[]>([]);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState<Label | null>(null);
  const [filters, setFilters] = useState<Record<string, any>>(() => {
    const out: Record<string, any> = {};
    if (initialUrlFilters.criteriaIds.length > 0) out.criteriaId = initialUrlFilters.criteriaIds;
    if (initialUrlFilters.typeIds.length > 0) out.type = initialUrlFilters.typeIds;
    if (initialUrlFilters.createdByIds.length > 0) out.createdBy = initialUrlFilters.createdByIds;
    if (initialUrlFilters.createdDate) out.createdDate = initialUrlFilters.createdDate;
    return out;
  });
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    severity: 'success' | 'error';
    message: string;
  }>({ open: false, severity: 'success', message: '' });

  // References to filter components for resetting
  const filterRefs = useRef<Record<string, ColumnFilterRef>>({});

  useEffect(() => {
    setIsLoadingData(true);
    const fetchData = async () => {
      try {
        const [
          labelsData,
          usersData,
          criteriaData,
          essaysData,
          purposesData,
          essayPurposesData,
          transcriptionsData,
        ] = await Promise.all([
          api.fetchLabels(),
          api.fetchUsers(),
          api.fetchCriteria(),
          api.fetchEssays(),
          api.fetchPurposes(),
          api.fetchEssayPurposes(),
          api.fetchTranscriptions(),
        ]);

        setLabels(labelsData);
        setUsers(usersData);
        setCriteria(criteriaData);
        setEssays(essaysData);
        setPurposes(purposesData);
        setEssayPurposes(essayPurposesData);
        setTranscriptions(transcriptionsData);

        if (initialUrlFilters.purposeIds.length > 0) {
          const relevantEssayIds = new Set(
            essayPurposesData
              .filter(ep => initialUrlFilters.purposeIds.includes(ep.purposeId))
              .map(ep => ep.essayId)
          );
          const relevantTranscriptionIds = transcriptionsData
            .filter(t => relevantEssayIds.has(t.essayId))
            .map(t => t.id);
          setFilters(prev => ({
            ...prev,
            transcriptionId:
              relevantTranscriptionIds.length > 0
                ? relevantTranscriptionIds
                : ['__NO_MATCHING_TRANSCRIPTIONS_FOR_PURPOSE__'],
          }));
        }
      } catch (error) {
        console.error('Error fetching label data:', error);
      } finally {
        setIsLoadingData(false);
      }
    };

    fetchData();
  }, [initialUrlFilters]);

  const handleViewLabel = useCallback((label: Label) => {
    setSelectedLabel(label);
    setViewDialogOpen(true);
  }, []);

  const handleSelectionChange = useCallback((ids: Array<string | number>) => {
    setSelectedIds(ids.map(id => String(id)));
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (selectedIds.length === 0) return;
    setIsDeleting(true);
    try {
      const { deleted } = await api.deleteLabelsBulk(selectedIds);
      const deletedSet = new Set(selectedIds);
      setLabels(prev => prev.filter(label => !deletedSet.has(label.id)));
      setSelectedIds([]);
      setDeleteDialogOpen(false);
      setSnackbar({
        open: true,
        severity: 'success',
        message: `Deleted ${deleted} label${deleted === 1 ? '' : 's'}`,
      });
    } catch (error) {
      console.error('Error deleting labels:', error);
      setSnackbar({
        open: true,
        severity: 'error',
        message: 'Failed to delete labels',
      });
    } finally {
      setIsDeleting(false);
    }
  }, [selectedIds]);

  const handleCloseViewDialog = useCallback(() => {
    setViewDialogOpen(false);
    setSelectedLabel(null);
  }, []);

  const handleFilterChange = useCallback((id: string, value: any) => {
    if (value === '') {
      setFilters(prev => {
        const newFilters = { ...prev };
        delete newFilters[id];
        return newFilters;
      });
    } else {
      setFilters(prev => ({ ...prev, [id]: value }));
    }
  }, []);

  const handleClearFilter = useCallback((filterId: string) => {
    setFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[filterId];
      return newFilters;
    });

    // If we have a ref to the filter component, reset its state
    if (filterRefs.current[filterId]) {
      filterRefs.current[filterId].reset();
    }
  }, []);

  const handleClearAllFilters = useCallback(() => {
    setFilters({});

    // Reset all filter components
    Object.values(filterRefs.current).forEach(ref => {
      if (ref) {
        ref.reset();
      }
    });
  }, []);

  // Helper functions
  const getUserName = useCallback(
    (userId: string) => {
      const user = users.find(u => u.id === userId);
      return user ? `${user.firstName} ${user.lastName}` : 'Unknown';
    },
    [users]
  );

  const getCriteriaName = useCallback(
    (criteriaId: string) => {
      const criteriaItem = criteria.find(c => c.id === criteriaId);
      return criteriaItem ? criteriaItem.name : 'Unknown';
    },
    [criteria]
  );

  const getEssayPurpose = useCallback(
    (essayId: string) => {
      // Find the essay purpose relationship
      const essayPurpose = essayPurposes.find(ep => ep.essayId === essayId);
      if (!essayPurpose) return 'N/A';

      // Find the purpose name
      const purpose = purposes.find(p => p.id === essayPurpose.purposeId);
      return purpose ? purpose.name : 'Unknown';
    },
    [essayPurposes, purposes]
  );

  const getTranscriptionDisplayLabelById = useCallback(
    (transcriptionId: string): string => {
      const transcription = transcriptions.find(t => t.id === transcriptionId);
      if (!transcription) return 'Unknown Transcription';

      const essay = essays.find(e => e.id === transcription.essayId);
      if (!essay) return transcription.versionName; // Fallback if essay not found for transcription

      const relevantEssayPurposes = essayPurposes.filter(ep => ep.essayId === essay.id);
      const purposeNames = relevantEssayPurposes
        .map(ep => purposes.find(p => p.id === ep.purposeId)?.name)
        .filter(name => !!name)
        .join(', ');

      return `${purposeNames || 'N/A'} - ${essay.learn_cycle_batch} - ${essay.paper_number} - ${transcription.versionName}`;
    },
    [transcriptions, essays, essayPurposes, purposes]
  );

  // Filter the labels based on the column filters
  const filteredLabels = useMemo(() => {
    return labels.filter(label => {
      // Essay filter (now based on transcription to essay)
      if (filters.transcriptionId && filters.transcriptionId !== '') {
        if (
          Array.isArray(filters.transcriptionId)
            ? !filters.transcriptionId.includes(label.transcriptionId)
            : label.transcriptionId !== filters.transcriptionId
        ) {
          return false;
        }
      }

      // Criteria filter
      if (
        filters.criteriaId &&
        filters.criteriaId !== '' &&
        (Array.isArray(filters.criteriaId)
          ? !filters.criteriaId.includes(label.criteriaId)
          : label.criteriaId !== filters.criteriaId)
      ) {
        return false;
      }

      // Type filter
      if (
        filters.type &&
        filters.type !== '' &&
        (Array.isArray(filters.type)
          ? !filters.type.includes(label.type)
          : label.type !== filters.type)
      ) {
        return false;
      }

      // Created by filter
      if (
        filters.createdBy &&
        filters.createdBy !== '' &&
        (Array.isArray(filters.createdBy)
          ? !filters.createdBy.includes(label.createdBy)
          : label.createdBy !== filters.createdBy)
      ) {
        return false;
      }

      // Created date filter
      if (filters.createdDate && label.createdDate) {
        const labelDate = new Date(label.createdDate);

        // Check start date if provided
        if (filters.createdDate.start && labelDate < new Date(filters.createdDate.start)) {
          return false;
        }

        // Check end date if provided
        if (filters.createdDate.end) {
          const endDate = new Date(filters.createdDate.end);
          // Set to end of day for inclusive comparison
          endDate.setHours(23, 59, 59, 999);
          if (labelDate > endDate) {
            return false;
          }
        }
      }

      return true;
    });
  }, [labels, filters]);

  // Filter label and description helpers
  const getFilterLabel = useCallback((filterId: string): string => {
    if (filterId === 'criteriaId') return 'Criteria';
    if (filterId === 'type') return 'Type';
    if (filterId === 'transcriptionId') return 'Transcription';
    if (filterId === 'createdBy') return 'Created By';
    if (filterId === 'createdDate') return 'Date';
    if (filterId === 'purposeId') return 'Purpose';
    return filterId;
  }, []);

  const getFilterDescription = useCallback(
    (filterId: string, value: any): string => {
      // For dropdown filters with single value
      if (typeof value === 'string') {
        if (filterId === 'criteriaId') {
          const criteriaItem = criteria.find(c => c.id === value);
          return criteriaItem ? criteriaItem.name : value;
        }
        if (filterId === 'type') {
          return value;
        }
        if (filterId === 'transcriptionId') {
          return getTranscriptionDisplayLabelById(value);
        }
        if (filterId === 'createdBy') {
          const user = users.find(u => u.id === value);
          return user ? `${user.firstName} ${user.lastName}` : value;
        }
        if (filterId === 'purposeId') {
          const purpose = purposes.find(p => p.id === value);
          return purpose ? purpose.name : value;
        }
      }

      // For dropdown filters with multiple values
      if (Array.isArray(value)) {
        return `${value.length} selected`;
      }

      // For date range filters
      if (filterId === 'createdDate' && value && typeof value === 'object') {
        let description = '';
        if (value.start) {
          description += `From ${new Date(value.start).toLocaleDateString()}`;
        }
        if (value.end) {
          description += value.start
            ? ` to ${new Date(value.end).toLocaleDateString()}`
            : `Until ${new Date(value.end).toLocaleDateString()}`;
        }
        return description;
      }

      return `${filterId}: ${JSON.stringify(value)}`;
    },
    [criteria, users, getTranscriptionDisplayLabelById, purposes]
  );

  // Register filter refs
  const registerFilterRef = useCallback((id: string, ref: ColumnFilterRef | null) => {
    if (ref) {
      filterRefs.current[id] = ref;
    }
  }, []);

  // Define the columns for the sortable table
  const headCells: HeadCell<Label>[] = [
    {
      id: 'transcription',
      label: 'Transcription',
      numeric: false,
      sortable: true,
      renderCell: (label: Label) => getTranscriptionDisplayLabelById(label.transcriptionId),
      getComparableValue: (label: Label) => getTranscriptionDisplayLabelById(label.transcriptionId),
      width: '25%', // Adjusted width for longer labels
      filter: (
        <ColumnFilter
          id="transcriptionId" // Will be updated to use transcriptionId
          label="Filter by Transcription"
          type="dropdown"
          options={transcriptions.map(t => ({
            id: t.id,
            label: getTranscriptionDisplayLabelById(t.id),
            value: t.id,
          }))}
          onChange={(id, value) => handleFilterChange('transcriptionId', value)} // Ensure this uses transcriptionId
          multiple={true}
          ref={ref => registerFilterRef('transcriptionId', ref)}
        />
      ),
    },
    {
      id: 'purpose',
      label: 'Purpose',
      numeric: false,
      sortable: true,
      renderCell: (label: Label) => {
        const transcription = transcriptions.find(t => t.id === label.transcriptionId);
        return transcription ? getEssayPurpose(transcription.essayId) : 'Unknown';
      },
      getComparableValue: (label: Label) => {
        const transcription = transcriptions.find(t => t.id === label.transcriptionId);
        return transcription ? getEssayPurpose(transcription.essayId) : 'Unknown';
      },
      width: '10%',
      filter: (
        <ColumnFilter
          id="purposeId"
          label="Filter by Purpose"
          type="dropdown"
          options={purposes.map(p => ({
            id: p.id,
            label: p.name,
            value: p.id,
          }))}
          onChange={(id, value) => {
            // Map purpose IDs to essay IDs for filtering
            if (value && value !== '' && (!Array.isArray(value) || value.length > 0)) {
              const relevantEssayIds = essayPurposes
                .filter(ep =>
                  Array.isArray(value) ? value.includes(ep.purposeId) : ep.purposeId === value
                )
                .map(ep => ep.essayId);

              const relevantTranscriptionIds = transcriptions
                .filter(t => relevantEssayIds.includes(t.essayId))
                .map(t => t.id);

              if (relevantTranscriptionIds.length > 0) {
                handleFilterChange('transcriptionId', relevantTranscriptionIds);
              } else {
                // If no transcriptions match the purpose, filter to show no results for transcriptionId
                // by passing a non-existent ID or a special marker if your filter handles it.
                // For simplicity, we can pass an empty array if multi-select transcription filter can show "none".
                // Or, to be absolutely sure no labels match, pass a unique ID that won't exist.
                handleFilterChange('transcriptionId', [
                  '__NO_MATCHING_TRANSCRIPTIONS_FOR_PURPOSE__',
                ]);
              }
            } else {
              // If purpose filter is cleared, we should ideally clear the transcriptionId filter *if* it was set by this purpose filter.
              // However, simply clearing it might remove a user's direct transcription filter.
              // A safer approach for now is to remove the specific 'transcriptionId' if it was set by this logic,
              // or do nothing and let user clear transcription filter manually.
              // For now, let's clear it if the purpose value is empty.
              const newFilters = { ...filters };
              delete newFilters['transcriptionId']; // Or, more targeted removal if possible.
              setFilters(newFilters);
              // Also reset the visual transcription filter component if it was manipulated by this
              if (filterRefs.current['transcriptionId']) {
                filterRefs.current['transcriptionId'].reset();
              }
            }
          }}
          multiple={true}
          initialValue={
            initialUrlFilters.purposeIds.length > 0 ? initialUrlFilters.purposeIds : undefined
          }
          ref={ref => registerFilterRef('purposeId', ref)}
        />
      ),
    },
    {
      id: 'criteria',
      label: 'Criteria',
      numeric: false,
      sortable: true,
      renderCell: label => getCriteriaName(label.criteriaId),
      getComparableValue: label => getCriteriaName(label.criteriaId),
      width: '20%',
      filter: (
        <ColumnFilter
          id="criteriaId"
          label="Filter by Criteria"
          type="dropdown"
          options={criteria
            .filter(
              c =>
                !c.isArchived ||
                (Array.isArray(filters.criteriaId)
                  ? filters.criteriaId.includes(c.id)
                  : filters.criteriaId === c.id)
            )
            .map(c => ({
              id: c.id,
              label: c.isArchived ? `${c.name} (Archived)` : c.name,
              value: c.id,
            }))}
          onChange={handleFilterChange}
          multiple={true}
          initialValue={
            initialUrlFilters.criteriaIds.length > 0 ? initialUrlFilters.criteriaIds : undefined
          }
          ref={ref => registerFilterRef('criteriaId', ref)}
        />
      ),
    },
    {
      id: 'type',
      label: 'Type',
      numeric: false,
      sortable: true,
      width: '15%',
      getComparableValue: label => label.type,
      renderCell: label => (
        <Chip
          label={label.type}
          sx={{
            backgroundColor: labelColors[label.type].main,
            color: 'rgba(0, 0, 0, 0.87)',
            border: `1px solid ${labelColors[label.type].border}`,
          }}
          size="small"
        />
      ),
      filter: (
        <ColumnFilter
          id="type"
          label="Filter by Type"
          type="dropdown"
          options={Array.from(new Set(labels.map(l => l.type))).map(type => ({
            id: type,
            label: type,
            value: type,
          }))}
          onChange={handleFilterChange}
          multiple={true}
          initialValue={
            initialUrlFilters.typeIds.length > 0 ? initialUrlFilters.typeIds : undefined
          }
          ref={ref => registerFilterRef('type', ref)}
        />
      ),
    },
    {
      id: 'createdBy',
      label: 'Created By',
      numeric: false,
      sortable: true,
      width: '15%',
      renderCell: label => getUserName(label.createdBy),
      getComparableValue: label => getUserName(label.createdBy),
      filter: (
        <ColumnFilter
          id="createdBy"
          label="Filter by User"
          type="dropdown"
          options={users.map(u => ({
            id: u.id,
            label: `${u.firstName} ${u.lastName}`,
            value: u.id,
          }))}
          onChange={handleFilterChange}
          multiple={true}
          initialValue={
            initialUrlFilters.createdByIds.length > 0 ? initialUrlFilters.createdByIds : undefined
          }
          ref={ref => registerFilterRef('createdBy', ref)}
        />
      ),
    },
    {
      id: 'createdDate',
      label: 'Created Date',
      numeric: false,
      sortable: true,
      width: '15%',
      getComparableValue: label => label.createdDate,
      renderCell: label =>
        new Date(label.createdDate).toLocaleString(undefined, {
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        }),
      filter: (
        <ColumnFilter
          id="createdDate"
          label="Date"
          type="date-range"
          onChange={handleFilterChange}
          initialValue={initialUrlFilters.createdDate ?? undefined}
          ref={ref => registerFilterRef('createdDate', ref)}
        />
      ),
    },
    {
      id: 'actions',
      label: '',
      numeric: false,
      sortable: false,
      width: '10%',
      renderCell: label => (
        <Button size="small" color="primary" onClick={() => handleViewLabel(label)}>
          View
        </Button>
      ),
    },
  ];

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Labels Management
      </Typography>

      {isLoadingData ? (
        <Loading />
      ) : (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 48, mb: 1 }}>
            {selectedIds.length > 0 && (
              <>
                <Typography variant="body1" sx={{ mr: 2 }}>
                  {selectedIds.length} selected
                </Typography>
                <Button
                  variant="contained"
                  color="error"
                  size="small"
                  startIcon={<DeleteIcon />}
                  onClick={() => setDeleteDialogOpen(true)}
                >
                  Delete
                </Button>
              </>
            )}
          </Box>
          <SortableTable
            data={filteredLabels}
            headCells={headCells}
            defaultSortBy="createdDate"
            defaultOrder="desc"
            idAccessor={label => label.id}
            emptyMessage="No labels found"
            showFilters={true}
            stickyHeader={true}
            filters={filters}
            onClearFilter={handleClearFilter}
            onClearAllFilters={handleClearAllFilters}
            getFilterLabel={getFilterLabel}
            getFilterDescription={getFilterDescription}
            totalCount={labels.length}
            pagination={true}
            defaultRowsPerPage={50}
            selectable={true}
            selectedIds={selectedIds}
            onSelectionChange={handleSelectionChange}
          />
        </>
      )}

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete labels?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete {selectedIds.length} label{selectedIds.length === 1 ? '' : 's'}? They will be
            hidden from the app but recoverable from the database.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error" disabled={isDeleting}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* View Label Dialog */}
      <Dialog open={viewDialogOpen} onClose={handleCloseViewDialog} maxWidth="md" fullWidth>
        <DialogTitle>Label Details</DialogTitle>
        <DialogContent>
          {selectedLabel && (
            <Paper style={{ padding: '20px' }}>
              <Typography variant="subtitle1">
                <strong>Transcription:</strong>{' '}
                {getTranscriptionDisplayLabelById(selectedLabel.transcriptionId)}
              </Typography>
              <Typography variant="subtitle1">
                <strong>Criteria:</strong> {getCriteriaName(selectedLabel.criteriaId)}
              </Typography>
              <Typography variant="subtitle1">
                <strong>Type:</strong>{' '}
                <Chip
                  label={selectedLabel.type}
                  sx={{
                    backgroundColor: labelColors[selectedLabel.type].main,
                    color: 'rgba(0, 0, 0, 0.87)',
                    border: `1px solid ${labelColors[selectedLabel.type].border}`,
                  }}
                  size="small"
                />
              </Typography>
              {selectedLabel.essayContentFragment && (
                <Typography variant="subtitle1">
                  <strong>Content Fragment:</strong> {selectedLabel.essayContentFragment}
                  {typeof selectedLabel.fragmentIndex === 'number' && (
                    <Chip
                      label={`#${selectedLabel.fragmentIndex}`}
                      size="small"
                      variant="outlined"
                      sx={{ ml: 1 }}
                    />
                  )}
                </Typography>
              )}
              {selectedLabel.reason && (
                <Typography variant="subtitle1">
                  <strong>Reason:</strong> {selectedLabel.reason}
                </Typography>
              )}
              <Typography variant="subtitle1">
                <strong>Created By:</strong> {getUserName(selectedLabel.createdBy)}
              </Typography>
              <Typography variant="subtitle1">
                <strong>Created Date:</strong>{' '}
                {new Date(selectedLabel.createdDate).toLocaleString(undefined, {
                  year: 'numeric',
                  month: 'numeric',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </Typography>
            </Paper>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default LabelsManagement;
